import { Color } from '@pixi/color';
import { Container, Graphics } from 'pixi.js-legacy';

import { themingInstance } from '@exchange/libs/utils/theming/src';

import TextValue from './pixi-text-value';
import { DataPointModel } from '../interfaces';

export default class FooterIndicator extends Container {
  public label: TextValue;

  private graphics = new Graphics();

  private xPos!: number;

  private yPos = 10;

  private xPadding = 5;

  private yPadding = 2;

  private tickWidth = 1;

  private gapBetweenLabels = 4;

  private cornersRadius = 4;

  private hasGreyBackground = false;

  private defaultBackgroundColor: number;

  private defaultColor: number;

  private askBackgroundColor: number;

  private askColor: number;

  private bidBackgroundColor: number;

  private bidColor: number;

  private isAsk: boolean;

  private xLimit: number;

  private containerBorderWidth: number;

  private getFormattedNumber: (value: number, isMarketPrecision?: boolean) => string;

  private get labelHeight() {
    return this.label.textHeight + this.yPadding * 2;
  }

  private get labelWidth() {
    return this.label.width + this.xPadding * 2;
  }

  private get isTopIndicator() {
    return this.hasGreyBackground || !this.isAsk;
  }

  private calculateY = () => (this.isTopIndicator ? this.yPos : this.yPos + this.labelHeight + this.gapBetweenLabels);

  constructor({
    fontName,
    isAsk,
    xLimit,
    containerBorderWidth,
    getFormattedNumber,
  }: {
    fontName: string;
    isAsk: boolean;
    xLimit: number;
    containerBorderWidth: number;
    getFormattedNumber: (value: number, isMarketPrecision?: boolean) => string;
  }) {
    super();

    this.isAsk = isAsk;
    this.xLimit = xLimit;
    this.containerBorderWidth = containerBorderWidth;
    this.getFormattedNumber = getFormattedNumber;
    const defaultColor = new Color(themingInstance.getColor('--v-theme-text-success')).toNumber();
    const defaultDarkColor = new Color(themingInstance.getColor('--text-0')).toNumber();

    this.defaultBackgroundColor = new Color(themingInstance.getColor('--v-theme-elevation-3')).toNumber();
    this.defaultColor = defaultColor;
    this.askBackgroundColor = new Color(themingInstance.getColor('--v-theme-error')).toNumber();
    this.askColor = defaultDarkColor;
    this.bidBackgroundColor = new Color(themingInstance.getColor('--v-theme-success')).toNumber();
    this.bidColor = defaultDarkColor;

    this.label = new TextValue({ fontName, text: '', light: this.isAsk });
    this.y = this.calculateY();
    this.zIndex = this.isTopIndicator ? 1 : 0;
    this.addChild(this.graphics, this.label);
    this.draw();
  }

  public update(pointerPosition: DataPointModel, hasGreyBackground: boolean, visible: boolean): void {
    this.visible = visible;
    this.hasGreyBackground = hasGreyBackground;

    if (!pointerPosition) {
      return;
    }

    if (pointerPosition.coordinates.x === 0 || +pointerPosition.size === 0) {
      this.visible = false;
    }

    if (+pointerPosition.size !== 0) {
      this.label.text = this.getFormattedNumber(Number(pointerPosition.size), false).toString();
      this.xPos = pointerPosition.coordinates.x;
    }

    if (this.xLimit < this.xPos + this.label.width / 2) {
      this.xPos = this.xLimit - this.label.width / 2;
    }

    this.label.x = this.xPos - this.label.width / 2;

    this.draw();
  }

  private draw() {
    this.graphics.clear();

    this.y = this.calculateY();

    if (this.hasGreyBackground) {
      this.label.tint = this.defaultColor;
      this.graphics.beginFill(this.defaultBackgroundColor);
    } else {
      this.label.tint = this.isAsk ? this.askColor : this.bidColor;
      this.graphics.beginFill(this.isAsk ? this.askBackgroundColor : this.bidBackgroundColor);
    }

    const topIndicatorTickYStart = this.yPos - this.containerBorderWidth;
    const topIndicatorTickYHeight = topIndicatorTickYStart - 1;
    const downIndicatorTickYStart = topIndicatorTickYStart + this.labelHeight + this.gapBetweenLabels;
    const downIndicatorTickHeight = topIndicatorTickYStart + 2 * this.labelHeight + this.gapBetweenLabels - topIndicatorTickYStart;

    const drawUpperTick = () => {
      if (this.xPos === undefined) {
        return;
      }

      this.graphics.drawRect(
        this.xPos,
        -1 * (this.isTopIndicator ? topIndicatorTickYStart : downIndicatorTickYStart),
        this.tickWidth,
        this.isTopIndicator ? topIndicatorTickYHeight : downIndicatorTickHeight,
      );
    };

    const drawColoredLabelBackground = () => {
      const shape = [this.xPos - this.label.width / 2 - this.xPadding, -this.yPadding, this.labelWidth, this.labelHeight] as const;

      this.graphics.drawRoundedRect(...shape, this.cornersRadius);
    };

    if (!this.hasGreyBackground) {
      drawUpperTick();
    }

    if (!this.hasGreyBackground) {
      drawColoredLabelBackground();
    }

    this.graphics.endFill();
  }
}
